import React, { useEffect, useRef, useState } from 'react';
import { ArtReproduction } from '../../types';
import { API_BASE } from '../../services/api';
import { extractDominantColors } from '../../utils/colorExtractor';
import { useArtworkDisplayUrl } from '../../hooks/useArtworkDisplayUrl';
import SaleBadge from '../SaleBadge';
import OptimizedImage from '../OptimizedImage';

interface ExtendedArtReproduction extends ArtReproduction {
  sizePrices?: Array<{ size: string; price: number }>;
  tags?: Array<{ id: number; name: string }>;
}

interface ArtworkCardProps {
  artwork: ExtendedArtReproduction;
  onOpen: (artwork: ExtendedArtReproduction) => void;
}

const ArtworkCard: React.FC<ArtworkCardProps> = ({
  artwork,
  onOpen
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const displayUrl = useArtworkDisplayUrl(artwork);

  // Defer color extraction until after image loads
  useEffect(() => {
    if (!imageLoaded || !cardRef.current) return;

    // Use requestIdleCallback for non-critical color extraction
    const loadColors = async (): Promise<void> => {
      try {
        const previewUrl = displayUrl || artwork.thumbnailUrl || artwork.imageUrl;
        const imageUrl = previewUrl.startsWith('http')
          ? previewUrl
          : `${API_BASE}${previewUrl}`;
        const extractedColors = await extractDominantColors(imageUrl, 4);
        if (cardRef.current && extractedColors.length > 0) {
          extractedColors.forEach((color, index) => {
            cardRef.current!.style.setProperty(`--theme-color-${index + 1}`, color);
          });
        }
      } catch (error) {
        // Silently fail - color extraction is non-critical
        console.debug('Color extraction deferred:', error);
      }
    };

    // Defer color extraction using requestIdleCallback or setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadColors, { timeout: 2000 });
    } else {
      setTimeout(loadColors, 100);
    }
  }, [imageLoaded, artwork.imageUrl, artwork.thumbnailUrl, displayUrl]);

  const handleImageLoad = (): void => {
    setImageLoaded(true);
  };

  return (
    <div
      ref={cardRef}
      className="artwork-card artwork-card--image-only"
      onClick={() => onOpen(artwork)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(artwork);
        }
      }}
    >
      <div className="artwork-image">
        <OptimizedImage
          src={artwork.imageUrl}
          thumbnailSrc={displayUrl || artwork.thumbnailUrl}
          alt={artwork.title || 'Artwork'}
          className="artwork-card-image"
          loadFullAboveWidth={768}
          onLoad={handleImageLoad}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        />
        {artwork.activeSale && <SaleBadge sale={artwork.activeSale} className="sale-badge--high-visibility" />}
        {artwork.isPinned && (
          <div className="artwork-pin-indicator" title="Pinned by artist">
            <i className="fas fa-thumbtack"></i>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtworkCard;
